// API client del Drive multi-tenant.
// Backend: mcp_django/apps/documents/ — endpoints REST con auth JWT.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  DocumentListFilters,
  DocumentOut,
  DocumentUploadIn,
  OrgUserOut,
  PublicLinkOut,
  ShareCreateIn,
  ShareOut,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/documents';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  return {
    Authorization: `Bearer ${token ?? ''}`,
  };
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status}: ${body}`);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ── List ──────────────────────────────────────────────

export async function listDocuments(
  filters: DocumentListFilters = {},
): Promise<DocumentOut[]> {
  const params = new URLSearchParams();
  if (filters.tab) params.set('tab', filters.tab);
  if (filters.file_type) params.set('file_type', filters.file_type);
  if (filters.status) params.set('status', filters.status);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${qs}`, {
    headers: await authHeaders(),
  });
  const data = await asJson<{ items: DocumentOut[] } | DocumentOut[]>(res);
  // Ninja paginate envuelve en {items, count}, sino lista plana
  return Array.isArray(data) ? data : data.items ?? [];
}

export async function getDocument(docId: string): Promise<DocumentOut> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${docId}`, {
    headers: await authHeaders(),
  });
  return asJson<DocumentOut>(res);
}

// ── Upload ────────────────────────────────────────────

export async function uploadDocument(
  input: DocumentUploadIn,
): Promise<DocumentOut> {
  const fd = new FormData();
  fd.append('title', input.title);
  fd.append('description', input.description ?? '');
  fd.append('tags', (input.tags ?? []).join(','));
  fd.append('visibility', input.visibility);
  fd.append('file', input.file);
  const res = await fetch(`${DJANGO_PROXY}${BASE}/`, {
    method: 'POST',
    headers: await authHeaders(), // NO Content-Type — el browser lo setea con boundary
    body: fd,
  });
  return asJson<DocumentOut>(res);
}

// ── Update metadata ───────────────────────────────────

export async function updateDocument(
  docId: string,
  patch: Partial<Pick<DocumentOut, 'title' | 'description' | 'tags' | 'visibility' | 'expires_at'>>,
): Promise<DocumentOut> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${docId}`, {
    method: 'PATCH',
    headers: {
      ...(await authHeaders()),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(patch),
  });
  return asJson<DocumentOut>(res);
}

// ── Delete (soft) ─────────────────────────────────────

export async function deleteDocument(docId: string): Promise<void> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${docId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await asJson<null>(res);
}

// ── Sharing ───────────────────────────────────────────

export async function createShare(
  docId: string,
  input: ShareCreateIn,
): Promise<ShareOut> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${docId}/share`, {
    method: 'POST',
    headers: {
      ...(await authHeaders()),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  return asJson<ShareOut>(res);
}

export async function listShares(docId: string): Promise<ShareOut[]> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${docId}/shares`, {
    headers: await authHeaders(),
  });
  return asJson<ShareOut[]>(res);
}

export async function revokeShare(docId: string, shareId: string): Promise<void> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${docId}/shares/${shareId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await asJson<null>(res);
}

// ── Org users (autocomplete ShareDialog) ──────────────

export async function listOrgUsers(q?: string): Promise<OrgUserOut[]> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  const res = await fetch(`${DJANGO_PROXY}${BASE}/org-users${qs}`, {
    headers: await authHeaders(),
  });
  return asJson<OrgUserOut[]>(res);
}

// ── Public link ───────────────────────────────────────

export async function createPublicLink(docId: string): Promise<PublicLinkOut> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${docId}/public-link`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return asJson<PublicLinkOut>(res);
}

export async function revokePublicLink(docId: string): Promise<void> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${docId}/public-link`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await asJson<null>(res);
}
